import { type ComponentType, lazy, Suspense, useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { AppShell } from '@/components/app-shell'
import { BrandLoader } from '@/components/brand-loader'
import { loadChunk, loadedChunk, type RouteChunk, type routeChunks } from '@/lib/route-chunks'

/**
 * The Suspense boundaries the lazy route pages load behind (#209): one per
 * outlet, so a page whose chunk is still on its way never leaves a blank window.
 *
 * A refresh is ONE loader: main.tsx preloads the page in the address bar and
 * RequireAuth holds the full-screen loader until its code has landed, so the
 * first paint after it is the whole page. A navigation keeps the page it is
 * leaving on screen while the next one loads (the router navigates in a
 * transition), and the nav prefetches on hover. What is left for the fallback
 * is a page nobody hovered, on a slow link — and even then the loader waits
 * FALLBACK_DELAY_MS, so a chunk that lands quickly flashes nothing.
 */

/** How long a Suspense fallback stays empty before the loader shows. */
export const FALLBACK_DELAY_MS = 200

/** Signed-in pages: the chrome is already up, the loader stands in for the page. */
export function AppOutlet() {
  return (
    <Suspense fallback={<AppPageLoader />}>
      <Outlet />
    </Suspense>
  )
}

/** Pages before sign-in (login, sign-up, the OAuth hand-offs): the loader, full screen. */
export function PublicOutlet() {
  return (
    <Suspense fallback={<DelayedBrandLoader size="page" />}>
      <Outlet />
    </Suspense>
  )
}

/** The shell, with the brand loader centred in the content area once the wait is real. */
function AppPageLoader() {
  return (
    <AppShell>
      <DelayedBrandLoader className="min-h-[60svh]" />
    </AppShell>
  )
}

/**
 * The brand loader, after FALLBACK_DELAY_MS. Before that the space is empty
 * (a page-sized background for `page`), so a fast load is seamless and a slow
 * one still says it is loading.
 */
export function DelayedBrandLoader({
  size,
  className,
}: {
  size?: 'page' | 'section'
  className?: string
}) {
  const [show, setShow] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setShow(true), FALLBACK_DELAY_MS)
    return () => clearTimeout(t)
  }, [])
  // It fades in when it shows, and hands over to the page when it lands.
  if (show) return <BrandLoader size={size} className={className} handoff />
  return <div aria-hidden className={size === 'page' ? 'min-h-svh bg-background' : className} />
}

/**
 * A route page loaded on demand: its module is its own chunk (lib/route-chunks).
 * Pages are named exports, so this picks the one the route renders.
 *
 * When the chunk has already landed — preloaded at boot, prefetched on hover —
 * the page renders straight from the module, with no Suspense at all: React.lazy
 * suspends on its first render even for a module in hand, and that one render is
 * a loader flashed over a page that was ready. The choice is fixed at mount, so
 * a page never swaps component types (and loses its state) mid-life.
 */
export function routePage<C extends RouteChunk>(
  chunk: C,
  name: keyof Awaited<ReturnType<(typeof routeChunks)[C]>> & string,
): ComponentType {
  const pick = (m: unknown) => (m as Record<string, ComponentType>)[name]
  const Lazy = lazy(() => loadChunk(chunk).then((m) => ({ default: pick(m) })))
  function RoutePage() {
    const [Page] = useState<ComponentType>(() => {
      const m = loadedChunk(chunk)
      return m ? pick(m) : Lazy
    })
    return <Page />
  }
  RoutePage.displayName = `RoutePage(${name})`
  return RoutePage
}
